import { useState, useEffect, useCallback } from 'react';
import { CHAT_EVENTS } from '../constants/chatEvents';
import { useChatSocket } from './useChatSocket';

/**
 * Senior-level Chat Logic Hook
 * Centralizes state management, socket interactions, and event handling.
 */
export const useChatLogic = (socket) => {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [typingUsers, setTypingUsers] = useState({}); // Map of chatId -> boolean
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 1. Initial Load & Sync
  useEffect(() => {
    if (socket?.connected) {
      socket.emit(CHAT_EVENTS.GET_CHAT_LIST, {});
    }
  }, [socket, socket?.connected]);

  // 2. Fetch Messages & Mark Read on Chat Switch
  useEffect(() => {
    if (socket?.connected && activeChat) {
      // Clear messages state before loading new ones
      setMessages([]);
      setPage(1);
      setHasMore(true);
      setIsLoadingMore(false);

      // Get message list for selected chat
      socket.emit(CHAT_EVENTS.GET_MSG_LIST, { chatId: activeChat.id, page: 1, limit: 10 });

      // Mark all messages as read
      // socket.emit(CHAT_EVENTS.READ_MSG, { chatId: activeChat.id });

      // Update local unread count for the active chat
      // setConversations(prev =>
      //   prev.map(c => c.id === activeChat.id ? { ...c, unreadCount: 0 } : c)
      // );

      return () => {
        if (socket?.connected) {
          socket.emit(CHAT_EVENTS.LEAVE_CHAT, { chatId: activeChat.id });
        }
      };
    }
  }, [socket, socket?.connected, activeChat?.id, activeChat]); // Use id to avoid unnecessary triggers

  /**
   * Socket Event Handlers
   */
  const handlers = {
    // Handle Chat List Response
    [CHAT_EVENTS.CHAT_LIST_DATA]: (data) => {
      setConversations(data || []);
    },

    [CHAT_EVENTS.SINGLE_CHAT]: (data) => {
      console.log("SINGLE_CHAT", data);
      if ('isTyping' in data) {
        setConversations(prev =>
          prev.map(c => c.id === data.id ? { ...c, isTyping: data.isTyping } : c)
        );
      } else {
        if (data?.object && Array.isArray(data?.object)) {
          const object = data?.object;
          const lastMsg = object[object.length - 1];
          setConversations(prev => {
            const updated = prev.map(conv => {
              if (String(conv.id) === String(lastMsg.chatId)) {
                return {
                  ...conv,
                  type: lastMsg.type,
                  lastMessageType: lastMsg.type,
                  lastMessage: lastMsg.text,
                  lastMessageTime: lastMsg.createdAt,
                  time: lastMsg.time || new Date(lastMsg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                  unreadCount: String(activeChat?.id) === String(lastMsg.chatId) ? 0 : (conv.unreadCount || 0) + object.length,
                  updatedAt: lastMsg.createdAt || new Date().toISOString()
                };
              }
              return conv;
            });

            return updated.sort((a, b) => {
              const tA = new Date(a.updatedAt || a.createdAt || 0).getTime();
              const tB = new Date(b.updatedAt || b.createdAt || 0).getTime();
              return tB - tA;
            });
          });
        } else {
          setConversations(prev =>
            prev.map(c => c.id === data.id ? data : c)
          );
        }
      }
    },

    [CHAT_EVENTS.RECEIVE_READ_MSG]: (data) => {
      setMessages(prev =>
        prev.map(c =>
          c.chatId === data.chatId
            ? { ...c, isRead: true }
            : c
        )
      );
    },

    // Handle Message List Response
    [CHAT_EVENTS.MSG_LIST_DATA]: (response) => {

      const list = Array.isArray(response)
        ? response
        : (response.data || response.messages || []);

      const sorted = [...list].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      
      setMessages(prev => {
        if (prev.length === 0) return sorted;
        const all = [...prev, ...sorted];
        const uniqueItems = new Map();
        for (const item of all) {
          uniqueItems.set(item.id, item);
        }
        return Array.from(uniqueItems.values()).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });

      setIsLoadingMore(false);
      if (list.length < 10) {
        setHasMore(false);
      }
    },

    // Handle incoming single or multiple messages
    // Handle incoming single or multiple messages
    [CHAT_EVENTS.RECEIVE_MSG]: (data) => {
      console.log("RECEIVE_MSG", data);
      // 1. Flexible parsing of incoming data
      const { chatId, messages: newMessages, message, object } = data;
      let incoming = [];
      if (Array.isArray(newMessages)) incoming = newMessages;
      else if (message) incoming = [message];
      else if (Array.isArray(data)) incoming = data;
      else if (data.text || data.id) incoming = [data]
      else if (Array.isArray(object)) incoming = object;

      if (incoming.length === 0) {
        console.warn('RECEIVE_MSG: No messages found in payload', data);
        return;
      }

      // 2. Identify which chat these messages belong to
      const targetChatId = chatId || incoming[0]?.chatId || data.chatId;
      if (!targetChatId) {
        console.warn('RECEIVE_MSG: Could not determine targetChatId', data);
        return;
      }

      // 3. Update messages list if it's the active chat
      if (activeChat && String(targetChatId) === String(activeChat.id)) {
        setMessages(prev => {
          let updated = [...prev, ...incoming];

          const seen = new Set();

          const unique = updated.filter(item => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          });

          // Keep sorted by creation date
          return unique.sort((a, b) => {
            const timeA = new Date(a.createdAt || a.time || 0).getTime();
            const timeB = new Date(b.createdAt || b.time || 0).getTime();
            return timeA - timeB;
          });
        });

        // socket.emit(CHAT_EVENTS.READ_MSG, { chatId: targetChatId });
      }

      // 4. Update the conversation preview in the sidebar
      const lastMsg = incoming[incoming.length - 1];
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (String(conv.id) === String(targetChatId)) {
            return {
              ...conv,
              type: lastMsg.type,
              lastMessageType: lastMsg.type,
              lastMessage: lastMsg.text,
              lastMessageTime: lastMsg.createdAt,
              time: lastMsg.time || new Date(lastMsg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              unreadCount: String(activeChat?.id) === String(targetChatId) ? 0 : (conv.unreadCount || 0) + incoming.length,
              updatedAt: lastMsg.createdAt || new Date().toISOString()
            };
          }
          return conv;
        });

        // Sort: most recent activities at the top
        return updated.sort((a, b) => {
          const tA = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const tB = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return tB - tA;
        });
      });
    },

    // Handle Typing Indicators
    [CHAT_EVENTS.TYPING_START]: ({ chatId }) => {
      setTypingUsers(prev => ({ ...prev, [chatId]: true }));
    },

    [CHAT_EVENTS.TYPING_STOP]: ({ chatId }) => {
      setTypingUsers(prev => ({ ...prev, [chatId]: false }));
    },
  };

  // Bind handlers
  useChatSocket(socket, handlers);

  /**
   * Actions
   */
  const sendMessage = useCallback((url, text, type = 'TEXT', extra = {}) => {
    if (!socket?.connected || !activeChat) {
      console.warn('Cannot send message: socket not connected or no active chat');
      return;
    }

    let payload;
    const tempId = `temp-${Date.now()}`;

    if (type !== 'TEXT') {
      payload = {
        chatId: activeChat.id,
        text,
        url,
        type,
        syncCode: extra.syncCode || tempId,
        ...extra
      };
    } else {
      payload = {
        chatId: activeChat.id,
        text,
        type,
        syncCode: extra.syncCode || tempId,
        ...extra
      };
    }
    console.log("send img", payload);
    socket.emit(CHAT_EVENTS.SEND_MSG, payload, (response) => {
      // If we get an immediate successful response with data, update the optimistic message
      const responseData = response?.data || (response?.id ? response : null);
      if (responseData) {
        setMessages(prev => prev.map(m =>
          (m.id === tempId || (payload.syncCode && m.syncCode === payload.syncCode))
            ? { ...responseData, isTemp: false }
            : m
        ));
      }
    });

  }, [socket, activeChat]);

  const findItem = useCallback((name) => {
    if (!socket?.connected || !name.trim()) {
      console.warn('Cannot send message: socket not connected or no active chat');
      return;
    }

    const payload = {
      itemName: name,
    };

    socket.emit(CHAT_EVENTS.FIND_ITEM, payload, (response) => {
      const responseData = response?.data || (response?.id ? response : null);
    });

  }, [socket]);

  const sendTypingStatus = useCallback((isTyping) => {
    if (!socket?.connected || !activeChat) return;
    const event = isTyping ? CHAT_EVENTS.TYPING_START : CHAT_EVENTS.TYPING_STOP;
    socket.emit(event, { chatId: activeChat.id });
  }, [socket, activeChat]);

  const loadMore = useCallback(() => {
    if (!socket?.connected || !activeChat || !hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);
    socket.emit(CHAT_EVENTS.GET_MSG_LIST, { chatId: activeChat.id, page: nextPage, limit: 10 });
  }, [socket, activeChat, hasMore, isLoadingMore, page]);

  return {
    conversations,
    messages,
    activeChat,
    setActiveChat,
    sendMessage,
    findItem,
    sendTypingStatus,
    isPartnerTyping: activeChat ? !!typingUsers[activeChat.id] : false,
    loadMore,
    hasMore,
    isLoadingMore,
  };
};
